import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, Package, Printer, FileDown, FileText, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../AuthContext';
import { openPrintWindow, buildPrintHTML } from '../../utils/printUtils';
import { exportToExcel } from '../../utils/excelExport';
import AssetDetailDialog from '../../components/AssetDetailDialog';
import DateRangeFilter, { filterByDateRange } from '../../components/DateRangeFilter';

const fmt = (n) => `Rs.${Number(n || 0).toLocaleString('en-IN')}`;
const dateIN = (s) => (s ? new Date(s).toLocaleDateString('en-IN') : '-');

const AssetRegisterReport = () => {
  const { isAdmin } = useAuth();
  const [branches, setBranches] = useState([]);
  const [data, setData] = useState(null);
  const [branch, setBranch] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [detailAsset, setDetailAsset] = useState(null);
  const [range, setRange] = useState({ from: null, to: null, label: 'All Time', presetKey: 'all' });

  useEffect(() => { api.get('/branches').then(r => setBranches(r.data)).catch(() => {}); }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = {};
      if (branch !== 'all') params.branch_id = branch;
      const resp = await api.get('/assets', { params });
      setData(resp.data);
    } catch { toast.error('Failed to fetch asset report'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchReport(); /* eslint-disable-next-line */ }, [branch]);

  const getBranchName = () => branch === 'all' ? 'All Branches' : branches.find(b => b.id === branch)?.name || 'Unknown';
  const baseAssets = data ? (typeFilter === 'all' ? data : data.filter(a => a.asset_type === typeFilter)) : [];
  const filteredAssets = filterByDateRange(baseAssets, range.from, range.to, 'purchase_date');

  // KPI aggregates
  const lineTotal = (a) => (a.total_purchase_value ?? ((a.purchase_price || 0) * (a.quantity ?? 1)));
  const totalPrice = filteredAssets.reduce((s, a) => s + lineTotal(a), 0);
  const totalCurrent = filteredAssets.reduce((s, a) => s + (a.current_value ?? lineTotal(a)), 0);
  const totalGst = filteredAssets.reduce((s, a) => s + (a.gst_amount || 0), 0);
  const withGst = filteredAssets.filter(a => a.gst_number).length;
  const withBill = filteredAssets.filter(a => a.has_bill).length;

  const handlePrint = () => {
    const rows = filteredAssets.map((a, i) => `<tr style="background:${i%2===0?'#fff':'#f9f9f9'}">
      <td>${a.asset_code}</td>
      <td>${a.asset_name}</td>
      <td>${a.asset_type === 'Other' ? (a.asset_type_other || 'Other') : a.asset_type}</td>
      <td>${a.category || '-'}</td>
      <td style="text-align:right">${a.quantity ?? 1}</td>
      <td>${a.branch_name || '-'}</td>
      <td>${dateIN(a.purchase_date)}</td>
      <td>${a.vendor_name || '-'}</td>
      <td>${a.bill_number || '-'}</td>
      <td>${dateIN(a.bill_date)}</td>
      <td style="font-family:monospace">${a.gst_number || '-'}</td>
      <td style="text-align:right">${a.gst_amount ? fmt(a.gst_amount) : '-'}</td>
      <td style="text-align:right">${fmt(a.purchase_price || 0)}</td>
      <td style="text-align:right">${fmt(lineTotal(a))}</td>
      <td style="text-align:right">${fmt(a.current_value ?? lineTotal(a))}</td>
      <td>${a.condition}</td>
      <td>${a.status}</td>
      <td style="text-align:center">${a.has_bill ? 'Yes' : 'No'}</td>
    </tr>`).join('');
    const summary = `<div class="cards">
      <div class="card"><small>Total Assets</small><b>${filteredAssets.length}</b></div>
      <div class="card"><small>Total Purchase Value</small><b>${fmt(totalPrice)}</b></div>
      <div class="card"><small>Current Book Value</small><b>${fmt(totalCurrent)}</b></div>
      <div class="card"><small>Total GST</small><b>${fmt(totalGst)}</b></div>
      <div class="card"><small>With GST No.</small><b>${withGst}</b></div>
      <div class="card"><small>Bill Uploaded</small><b>${withBill}</b></div>
    </div>`;
    const body = `${summary}<table><thead><tr><th>Code</th><th>Name</th><th>Type</th><th>Category</th><th>Qty</th><th>Branch</th><th>Purchase Date</th><th>Vendor</th><th>Bill No.</th><th>Bill Date</th><th>GST No.</th><th>GST Amt</th><th>Unit Price</th><th>Total Value</th><th>Current Value</th><th>Condition</th><th>Status</th><th>Bill?</th></tr></thead><tbody>${rows}</tbody></table>`;
    openPrintWindow(buildPrintHTML(`Asset Register — ${range.label}`, body, getBranchName()));
  };

  const handleExcel = () => {
    if (filteredAssets.length === 0) { toast.error('No assets to export'); return; }
    const rows = filteredAssets.map((a, i) => ({
      'S.No.': i + 1,
      'Asset Code': a.asset_code || '',
      'Asset Name': a.asset_name || '',
      'Type': a.asset_type === 'Other' ? (a.asset_type_other || 'Other') : a.asset_type,
      'Category': a.category || '',
      'Quantity': a.quantity ?? 1,
      'Branch': a.branch_name || '',
      'Purchase Date': a.purchase_date ? new Date(a.purchase_date).toLocaleDateString('en-IN') : '',
      'Vendor': a.vendor_name || '',
      'Bill No.': a.bill_number || '',
      'Bill Date': a.bill_date ? new Date(a.bill_date).toLocaleDateString('en-IN') : '',
      'GST No.': a.gst_number || '',
      'GST Amount (Rs.)': Number(a.gst_amount || 0),
      'Unit Price (Rs.)': Number(a.purchase_price || 0),
      'Total Purchase Value (Rs.)': Number(lineTotal(a)),
      'Current Value (Rs.)': Number(a.current_value ?? lineTotal(a)),
      'Condition': a.condition || '',
      'Status': a.status || '',
      'Bill Uploaded': a.has_bill ? 'Yes' : 'No',
      'Location': a.location || '',
      'Remarks': a.remarks || '',
    }));
    exportToExcel({
      filename: `WWAGS_Asset_Register_${getBranchName().replace(/\s+/g, '_')}`,
      sheetName: 'Asset Register',
      rows,
    });
  };

  const handleCsv = () => {
    const headers = ['Asset Code','Asset Name','Type','Category','Qty','Branch','Purchase Date','Vendor','Bill No','Bill Date','GST No','GST Amount','Unit Price','Total Purchase Value','Current Value','Condition','Status','Bill Uploaded','Location','Remarks'];
    const rows = filteredAssets.map(a => [
      a.asset_code, a.asset_name,
      a.asset_type === 'Other' ? (a.asset_type_other || 'Other') : a.asset_type,
      a.category || '', a.quantity ?? 1, a.branch_name || '',
      a.purchase_date ? new Date(a.purchase_date).toLocaleDateString('en-IN') : '',
      a.vendor_name || '', a.bill_number || '',
      a.bill_date ? new Date(a.bill_date).toLocaleDateString('en-IN') : '',
      a.gst_number || '', a.gst_amount || 0,
      a.purchase_price || 0, lineTotal(a), a.current_value ?? lineTotal(a),
      a.condition || '', a.status || '',
      a.has_bill ? 'Yes' : 'No', a.location || '', a.remarks || '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Asset_Register_${getBranchName().replace(/\s+/g,'_')}_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="asset-register-report-page">
      <div className="flex items-center gap-3 mb-4">
        <Link to="/reports" className="text-slate-500 hover:text-slate-700"><ArrowLeft className="w-5 h-5" /></Link>
        <div className="flex-1"><h1 className="text-2xl font-bold text-slate-900">Asset Register</h1><p className="text-sm text-slate-600">Branch-wise asset list with vendor, GST & bill details. Click an Asset Code to view full details.</p></div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-3 md:p-4 mb-4 flex flex-wrap items-end gap-3">
        {isAdmin() && (
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Branch</span>
            <Select value={branch} onValueChange={setBranch}><SelectTrigger className="w-48 h-9" data-testid="branch-filter"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All Branches</SelectItem>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.code} - {b.name}</SelectItem>)}</SelectContent></Select>
          </div>
        )}
        <div className="flex flex-col gap-1">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Type</span>
          <Select value={typeFilter} onValueChange={setTypeFilter}><SelectTrigger className="w-40 h-9" data-testid="type-filter"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All Types</SelectItem>{['Electronic','Furniture','Land','Vehicle','Building','Other'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
        </div>
        <DateRangeFilter value={range} onChange={setRange} />
        <div className="flex gap-2 ml-auto">
          {filteredAssets.length > 0 && <Button variant="outline" onClick={handlePrint} data-testid="print-btn"><Printer className="w-4 h-4 mr-1" />Print</Button>}
          {filteredAssets.length > 0 && <Button variant="outline" onClick={handleExcel} data-testid="excel-btn" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"><FileSpreadsheet className="w-4 h-4 mr-1" />Excel</Button>}
          {filteredAssets.length > 0 && <Button variant="outline" onClick={handleCsv} data-testid="csv-btn"><FileDown className="w-4 h-4 mr-1" />CSV</Button>}
        </div>
      </div>

      {/* KPI cards */}
      {filteredAssets.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 mb-4">
          <Kpi label="Total Assets" value={filteredAssets.length} color="slate" />
          <Kpi label="Purchase Value" value={fmt(totalPrice)} color="blue" />
          <Kpi label="Current Book Value" value={fmt(totalCurrent)} color="emerald" />
          <Kpi label="Total GST" value={fmt(totalGst)} color="amber" />
          <Kpi label="With GST No." value={withGst} color="purple" />
          <Kpi label="Bill Uploaded" value={withBill} color="amber" />
        </div>
      )}

      {loading && <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-700"></div></div>}

      {!loading && filteredAssets.length > 0 && (
        <div className="overflow-x-auto bg-white rounded-lg border border-slate-200">
          <table className="w-full text-xs">
            <thead className="bg-teal-700 text-white">
              <tr>
                <th className="px-3 py-2 text-left">Code</th>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-center">Qty</th>
                <th className="px-3 py-2 text-left">Branch</th>
                <th className="px-3 py-2 text-center">Purchase Date</th>
                <th className="px-3 py-2 text-left">Vendor</th>
                <th className="px-3 py-2 text-left">Bill No.</th>
                <th className="px-3 py-2 text-center">Bill Date</th>
                <th className="px-3 py-2 text-left">GST No.</th>
                <th className="px-3 py-2 text-right">GST Amt</th>
                <th className="px-3 py-2 text-right">Unit Price</th>
                <th className="px-3 py-2 text-right">Total Value</th>
                <th className="px-3 py-2 text-right">Current Value</th>
                <th className="px-3 py-2 text-center">Condition</th>
                <th className="px-3 py-2 text-center">Bill?</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredAssets.map((asset, i) => (
                <tr key={asset.id} className={i % 2 === 0 ? '' : 'bg-slate-50'}>
                  <td className="px-3 py-2 font-mono">
                    <button
                      onClick={() => setDetailAsset(asset)}
                      className="text-blue-700 hover:underline font-semibold"
                      data-testid={`view-asset-${asset.id}`}
                    >
                      {asset.asset_code}
                    </button>
                  </td>
                  <td className="px-3 py-2 font-medium">
                    <button onClick={() => setDetailAsset(asset)} className="text-left hover:text-blue-700 hover:underline">
                      {asset.asset_name}
                    </button>
                  </td>
                  <td className="px-3 py-2">{asset.asset_type === 'Other' ? (asset.asset_type_other || 'Other') : asset.asset_type}</td>
                  <td className="px-3 py-2 text-center font-medium text-slate-800">{asset.quantity ?? 1}</td>
                  <td className="px-3 py-2 text-slate-600">{asset.branch_name || '-'}</td>
                  <td className="px-3 py-2 text-center">{dateIN(asset.purchase_date)}</td>
                  <td className="px-3 py-2">{asset.vendor_name || <span className="text-slate-400 italic">-</span>}</td>
                  <td className="px-3 py-2">{asset.bill_number || <span className="text-slate-400 italic">-</span>}</td>
                  <td className="px-3 py-2 text-center">{dateIN(asset.bill_date)}</td>
                  <td className="px-3 py-2 font-mono text-[11px]">{asset.gst_number || <span className="text-slate-400 italic">-</span>}</td>
                  <td className="px-3 py-2 text-right">{asset.gst_amount ? fmt(asset.gst_amount) : '-'}</td>
                  <td className="px-3 py-2 text-right">{fmt(asset.purchase_price || 0)}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="font-medium">{fmt(lineTotal(asset))}</div>
                    {(asset.quantity ?? 1) > 1 && (
                      <div className="text-[10px] text-slate-500">{fmt(asset.purchase_price || 0)} × {asset.quantity}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right text-emerald-700 font-medium">{fmt(asset.current_value ?? lineTotal(asset))}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={`px-2 py-0.5 rounded-full ${asset.condition === 'Good' ? 'bg-emerald-100 text-emerald-700' : asset.condition === 'Fair' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                      {asset.condition}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-center">
                    {asset.has_bill ? (
                      <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">
                        <FileText className="w-2.5 h-2.5" /> Yes
                      </span>
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && filteredAssets.length === 0 && <div className="text-center py-12 text-slate-500"><Package className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p>No assets found for selected filters</p></div>}

      <AssetDetailDialog asset={detailAsset} onClose={() => setDetailAsset(null)} />
    </div>
  );
};

const Kpi = ({ label, value, color }) => {
  const map = {
    slate: 'bg-slate-50 border-slate-200 text-slate-800',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
    purple: 'bg-purple-50 border-purple-200 text-purple-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
  };
  return (
    <div className={`rounded-lg border p-3 ${map[color]}`}>
      <p className="text-[10px] uppercase tracking-wide font-semibold opacity-70">{label}</p>
      <p className="text-xl font-bold mt-1 truncate">{value}</p>
    </div>
  );
};

export default AssetRegisterReport;
